// ── Work Item Intake API Client (ClientApp/src/api/intake-v1.ts) ──
// User-facing work-request surface. Calls go through the AskIAM BFF proxy at
// /api/work-items/intake/*, which is gated by the "Authenticated" policy.

import { fetchJson, fetchJsonWithMeta, cachedFetchJson, postJson } from './client';
import type { WithMeta } from './client';
import type {
  RoutingNode,
  CustomFieldDefinition,
  RoutingTableEntry,
  EnvironmentEntry,
  IntakeItem,
  WorkRequestSubmitInput,
  TeamRegistration,
  TeamDirectoryEntry,
  DestinationConfig,
  TriageInput,
  TriageResult,
  SuggestionResponse,
  ReviewRequest,
  ReviewResult,
  CompletenessResult,
  QuickAction,
  MoveTeamInput,
  MoveTeamResult,
  ReopenInput,
  ReopenResult,
  MarkUnderReviewInput,
  MarkUnderReviewResult,

  TriageAgingSummary,
  IntakeFunnelSummary,
  RoutingAndDeclineSummary,
  DeclinedDetailSummary,
  DeflectionEffectivenessSummary,
  HotSpotReportSummary,
  CrossSurfaceHotSpotSummary,
  TriageLagTrendSummary,
  TriageTimingSummary,
  ExecutiveOverviewSummary,
  DataQualitySummary,
} from '../types/work-item-v1';
import type { WorkRequestMessageVisibility } from '../types/work-item-v1';
import type { IntakeResponseArtifact } from '../types/intake-artifact';

const ROOT = '/api/work-items/intake';

/**
 * The routing taxonomy, environment list and routable (Product × Environment) set are
 * platform-owned reference data, edited only in Routing Admin. Both the submit page and the
 * triage backlog request all three together on every mount, so they are cached for the session
 * rather than re-fetched per visit.
 *
 * Held longer than team-scoped data because nothing on these pages can change them; a Routing
 * Admin edit clears the whole work-items cache, so a change is still picked up at once.
 */
const REFERENCE_TTL_MS = 300_000;

export function getRoutingTree(): Promise<RoutingNode[]> {
  return cachedFetchJson<RoutingNode[]>(`${ROOT}/routing-tree`, REFERENCE_TTL_MS);
}

/** A team- or admin-managed external link, rendered as a launcher tile. */
export interface TeamResourceTile {
  id: string;
  title: string;
  description: string;
  url: string;
  category?: string | null;
  teamName?: string | null;
  /** Which launcher the tile lands on: 'request' (Request Central) or 'exception' (Exception Central). */
  surface?: string | null;
  /** Icon key from the shared tile icon set. */
  icon?: string | null;
}

/**
 * Active team- and admin-managed resources. These replace templates carrying prodStatus="external":
 * a team (or an admin, for platform-wide links with no owning team) owns its own links rather than
 * needing a template checked in.
 */
export function listTeamResources(): Promise<TeamResourceTile[]> {
  return fetchJson<TeamResourceTile[]>(`${ROOT}/team-resources`);
}

export function suggestRouting(query: string, top = 8, environmentId?: string): Promise<SuggestionResponse> {
  return postJson<SuggestionResponse>(`${ROOT}/suggest`, { query, top, environmentId });
}

// ── Preliminary review (advisory deflection funnel, Gates 1-3) ──

export function reviewRequest(input: ReviewRequest): Promise<ReviewResult> {
  return postJson<ReviewResult>(`${ROOT}/review`, input);
}

// Gate 3 on its own. The submit form no longer calls this — it runs the full /review from Analyze
// and reads completeness off that result — but the endpoint remains for callers that want the
// completeness verdict without paying for deflection matching.
export function checkCompleteness(input: ReviewRequest): Promise<CompletenessResult> {
  return postJson<CompletenessResult>(`${ROOT}/completeness`, input);
}

export function recordReviewChoice(reviewId: string, userChoice: string, chosenTargetValue?: string): Promise<void> {
  return postJson<void>(`${ROOT}/review/${encodeURIComponent(reviewId)}/choice`, { userChoice, chosenTargetValue });
}

export function recordReviewFeedback(reviewId: string, targetValue: string, helpful: boolean): Promise<void> {
  return postJson<void>(`${ROOT}/review/${encodeURIComponent(reviewId)}/feedback`, { targetValue, helpful });
}

export function linkReviewWorkRequest(reviewId: string, workRequestId: string): Promise<void> {
  return postJson<void>(`${ROOT}/review/${encodeURIComponent(reviewId)}/link`, { workRequestId });
}

export function getProductCustomFields(productNodeId: string): Promise<CustomFieldDefinition[]> {
  return fetchJson<CustomFieldDefinition[]>(`${ROOT}/products/${encodeURIComponent(productNodeId)}/custom-fields`);
}

export function submitWorkRequest(input: WorkRequestSubmitInput): Promise<IntakeItem> {
  return postJson<IntakeItem>(`${ROOT}/work-requests`, input);
}

export function listEnvironments(): Promise<EnvironmentEntry[]> {
  return cachedFetchJson<EnvironmentEntry[]>(`${ROOT}/environments`, REFERENCE_TTL_MS);
}

export function listRoutable(): Promise<RoutingTableEntry[]> {
  return cachedFetchJson<RoutingTableEntry[]>(`${ROOT}/routable`, REFERENCE_TTL_MS);
}

export function listWhereICanTriage(): Promise<WithMeta<TeamRegistration[]>> {
  return fetchJsonWithMeta<TeamRegistration[]>(`${ROOT}/teams/where-i-can-triage`);
}

/**
 * A catalogue-wide access answer. Three-valued because a FORGE outage leaves the server unable to
 * check part of the catalogue, and reporting that as `denied` would hide a nav entry from someone
 * whose access is intact — the same lie the 503 work exists to stop telling.
 *
 * Strings rather than booleans on purpose: a stale client fails visibly on an unexpected value
 * instead of coercing `"unknown"` to truthy and pretending it understood.
 */
export type CatalogueGrant = 'granted' | 'denied' | 'unknown';

/**
 * Whether the caller has any team surface at all. Both the Team Backlog and Manage Team
 * screens are membership-driven rather than role-driven, so the left nav cannot decide
 * their visibility from roles alone and has to ask the server.
 */
export interface MyTeamAccess {
  canTriage: CatalogueGrant;
  canManage: CatalogueGrant;
}

export function getMyTeamAccess(): Promise<MyTeamAccess> {
  return fetchJson<MyTeamAccess>(`${ROOT}/teams/my-access`);
}

/**
 * Active teams (id + display name only) that a triager may reroute to directly.
 * Restricted server-side to callers who can triage somewhere.
 */
export function listTeamDirectory(): Promise<TeamDirectoryEntry[]> {
  return fetchJson<TeamDirectoryEntry[]>(`${ROOT}/teams/directory`);
}

/**
 * Active Quick Action presets available from the team backlog.
 * Restricted server-side to callers who can triage somewhere.
 */
export function listQuickActions(): Promise<QuickAction[]> {
  return fetchJson<QuickAction[]>(`${ROOT}/quick-actions`);
}

export function getTeamBacklog(teamId: string): Promise<IntakeItem[]> {
  return fetchJson<IntakeItem[]>(`${ROOT}/teams/${encodeURIComponent(teamId)}/backlog`);
}

export function getTeamHistory(teamId: string): Promise<IntakeItem[]> {
  return fetchJson<IntakeItem[]>(`${ROOT}/teams/${encodeURIComponent(teamId)}/history`);
}

export function getTeamTriageAging(teamId: string): Promise<TriageAgingSummary> {
  return fetchJson<TriageAgingSummary>(`${ROOT}/teams/${encodeURIComponent(teamId)}/reports/triage-aging`);
}

// C1 — intake funnel. Org-wide, not team-scoped (see WorkItemEndpoints.cs), so there is no
// teamId in this route.
export function getIntakeFunnelReport(windowDays: number): Promise<IntakeFunnelSummary> {
  return fetchJson<IntakeFunnelSummary>(`${ROOT}/reports/intake-funnel?windowDays=${encodeURIComponent(windowDays)}`);
}

export function getRoutingReport(windowDays: number, periodDays: number, topPairsCount: number): Promise<RoutingAndDeclineSummary> {
  const qs = new URLSearchParams({
    windowDays: String(windowDays),
    periodDays: String(periodDays),
    topPairsCount: String(topPairsCount),
  });
  return fetchJson<RoutingAndDeclineSummary>(`${ROOT}/reports/routing?${qs.toString()}`);
}

/**
 * C5 — denied and redirected detail. `category` takes a C4 bucket key (including `unrecognized`)
 * and `action` takes `deny` or `redirect`, so a C4 bar can open exactly its own rows. Both are
 * rejected server-side when unknown rather than silently ignored.
 */
export function getDeclinedDetailReport(
  windowDays: number,
  rowLimit: number,
  category?: string,
  action?: string,
): Promise<DeclinedDetailSummary> {
  const qs = new URLSearchParams({
    windowDays: String(windowDays),
    rowLimit: String(rowLimit),
  });
  if (category) qs.set('category', category);
  if (action) qs.set('action', action);
  return fetchJson<DeclinedDetailSummary>(`${ROOT}/reports/declined-detail?${qs.toString()}`);
}

export function getDeflectionEffectivenessReport(windowDays: number): Promise<DeflectionEffectivenessSummary> {
  return fetchJson<DeflectionEffectivenessSummary>(
    `${ROOT}/reports/deflection-effectiveness?windowDays=${encodeURIComponent(windowDays)}`,
  );
}

export function getExecutiveOverviewReport(windowDays: number): Promise<ExecutiveOverviewSummary> {
  return fetchJson<ExecutiveOverviewSummary>(`${ROOT}/reports/overview?windowDays=${encodeURIComponent(windowDays)}`);
}

export function getHotSpotReport(windowDays: number, environmentId?: string): Promise<HotSpotReportSummary> {
  const params = new URLSearchParams({ windowDays: String(windowDays) });
  if (environmentId) params.set('environmentId', environmentId);
  return fetchJson<HotSpotReportSummary>(`${ROOT}/reports/hot-spots?${params.toString()}`);
}

export function getCrossSurfaceHotSpotReport(windowDays: number, environmentId?: string): Promise<CrossSurfaceHotSpotSummary> {
  const params = new URLSearchParams({ windowDays: String(windowDays) });
  if (environmentId) params.set('environmentId', environmentId);
  return fetchJson<CrossSurfaceHotSpotSummary>(`${ROOT}/reports/cross-surface-hot-spots?${params.toString()}`);
}

export function getDataQualityReport(windowDays: number): Promise<DataQualitySummary> {
  return fetchJson<DataQualitySummary>(`${ROOT}/reports/data-quality?windowDays=${encodeURIComponent(windowDays)}`);
}

export function getTriageLagTrendReport(windowDays: number, periodDays: number): Promise<TriageLagTrendSummary> {
  const params = new URLSearchParams({ windowDays: String(windowDays), periodDays: String(periodDays) });
  return fetchJson<TriageLagTrendSummary>(`${ROOT}/reports/triage-lag?${params.toString()}`);
}

/** E3/E4 — queue time vs deliberation time, and dwell per reroute hop. One window control. */
export function getTriageTimingReport(windowDays: number): Promise<TriageTimingSummary> {
  return fetchJson<TriageTimingSummary>(`${ROOT}/reports/triage-timing?windowDays=${encodeURIComponent(windowDays)}`);
}

export function getTeamDestinations(teamId: string): Promise<DestinationConfig[]> {
  return fetchJson<DestinationConfig[]>(`${ROOT}/teams/${encodeURIComponent(teamId)}/destinations`);
}

export function triageWorkRequest(id: string, input: TriageInput): Promise<TriageResult> {
  return postJson<TriageResult>(`${ROOT}/work-requests/${encodeURIComponent(id)}/triage`, input);
}

/**
 * Move a work request to another team without changing its triage outcome. Used to rehouse the
 * history a retiring team accumulated onto its FORGE-anchored replacement. Server-side rules
 * (see f14b-move-team-design.md) allow this only for a terminal request, or for a live request
 * whose owning team is no longer live (admin-only).
 */
export function moveWorkRequestTeam(id: string, input: MoveTeamInput): Promise<MoveTeamResult> {
  return postJson<MoveTeamResult>(`${ROOT}/work-requests/${encodeURIComponent(id)}/move-team`, input);
}

/**
 * Return a settled work request to its owning team's backlog so it can be triaged again. The
 * server accepts this only for a denied or redirected request (`WorkRequestStatus.Reopenable`) and
 * requires the caller to hold TriageWork on that team, or to be a platform admin.
 */
export function reopenWorkRequest(id: string, input: ReopenInput): Promise<ReopenResult> {
  return postJson<ReopenResult>(`${ROOT}/work-requests/${encodeURIComponent(id)}/reopen`, input);
}

/**
 * Tell the requestor that the owning team is actively looking at a request that has not yet been
 * decided. The request stays `submitted` — this writes only the review marker — and the call is
 * idempotent, so re-marking an already-marked request succeeds without moving the timestamp.
 */
export function markWorkRequestUnderReview(id: string, input: MarkUnderReviewInput): Promise<MarkUnderReviewResult> {
  return postJson<MarkUnderReviewResult>(`${ROOT}/work-requests/${encodeURIComponent(id)}/review`, input);
}

export function listMyWorkRequests(): Promise<IntakeItem[]> {
  return fetchJson<IntakeItem[]>(`${ROOT}/work-requests/mine`);
}

export function getMyWorkRequest(id: string): Promise<IntakeItem> {
  return fetchJson<IntakeItem>(`${ROOT}/work-requests/${encodeURIComponent(id)}`);
}

/**
 * The frozen structured record of the submission behind a work request.
 *
 * Not under {@link ROOT}: the artifact endpoints are served directly by AskIAM rather than proxied
 * to the intake service, because the artifact is generated and stored by AskIAM at submit time.
 *
 * 404 means the work request's template does not produce an artifact (or predates the feature);
 * 403 means the caller may not read it. Both are ordinary outcomes for this page.
 */
export function getWorkRequestArtifact(workRequestId: string, revision?: number): Promise<IntakeResponseArtifact> {
  const query = revision ? `?revision=${encodeURIComponent(String(revision))}` : '';
  return fetchJson<IntakeResponseArtifact>(
    `/api/work-requests/${encodeURIComponent(workRequestId)}/artifact${query}`,
  );
}

/**
 * Appends a message to a work request's conversation thread. `Internal` is only accepted from
 * triagers (owning-team owners/delegates and platform admins); the server rejects it with 403
 * for anyone else. Returns the updated request as the caller is permitted to see it.
 */
export function postWorkRequestMessage(
  id: string,
  body: string,
  visibility: WorkRequestMessageVisibility = 'Public',
): Promise<IntakeItem> {
  return postJson<IntakeItem>(`${ROOT}/work-requests/${encodeURIComponent(id)}/conversation`, {
    body,
    visibility,
  });
}
